import React, { useEffect } from 'react';

/**
 * SMS Terms and Privacy Policy page.
 * Full legal text for SMS messaging compliance.
 *
 * @returns {JSX.Element} Public SMS terms content.
 */
const SMSTerms = () => {
  useEffect(() => {
    document.title = "SMS Terms & Privacy - R&S App";
  }, []);

  return (
    <div className="min-h-screen bg-custom-rsbg py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Terms and Conditions Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h1 className="text-3xl font-bold mb-6">Terms and Conditions</h1>

          <h2 className="text-xl font-semibold mt-6 mb-3">Description</h2>
          <p className="text-gray-700 mb-4">
            These terms and conditions cover the usage of your phone number to send automated text
            messages from the R&S Construction App to the number you have provided. These texts will
            be used only to send scheduling messages, which state when and where your next shift is,
            along with other business and operational messages.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Cancellation</h2>
          <p className="text-gray-700 mb-4">
            You can cancel the SMS service at any time. Simply text "STOP" to the message. Upon
            sending "STOP," we will confirm your unsubscribe status via SMS. Following this
            confirmation, you will no longer receive SMS messages from us. To re-join, sign up as
            you did initially, and we will resume sending SMS messages to you.
          </p>
          <p className="text-gray-700 mb-4">
            If you experience issues with the messaging program, reply with the keyword HELP for
            more assistance, or reach out directly to info@rscminc.com.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Liability</h2>
          <p className="text-gray-700 mb-4">
            Carriers are not liable for delayed or undelivered messages.
          </p>
          <p className="text-gray-700 mb-4">
            As always, message and data rates may apply for messages sent to you from us and to us
            from you. You will receive 1 message per scheduled shift. On occasion, we may send you
            other messages related to your employment at the company. For questions about your text
            plan or data plan, contact your wireless provider.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Privacy</h2>
          <p className="text-gray-700 mb-4">
            For privacy-related inquiries, please refer to our privacy policy below.
          </p>
        </div>

        {/* Privacy Policy Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

          <h2 className="text-xl font-semibold mt-6 mb-3">Introduction</h2>
          <p className="text-gray-700 mb-4">
            This Privacy Policy explains how R&S Construction Management Inc uses, discloses, and
            protects personal information provided by individuals who opt-in for Application-to-Person
            (A2P) text messaging services. We are committed to protecting your privacy and ensuring
            that your personal information is handled in accordance with applicable data protection
            and privacy laws.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Information We Collect</h2>
          <p className="text-gray-700 mb-4">
            When you opt-in for our A2P text messaging services, we may collect the following types
            of personal information:
          </p>
          <p className="text-gray-700 mb-2">
            <em>Contact Information</em>: Your mobile phone number.
          </p>
          <p className="text-gray-700 mb-2">
            <em>User Preferences</em>: Any preferences or settings you choose to provide for
            customization of your text messaging experience.
          </p>
          <p className="text-gray-700 mb-4">
            <em>Transactional Information</em>: Information related to your interactions with our A2P
            text messaging services, including the content of messages sent and received.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Opt-in Consent</h2>
          <p className="text-gray-700 mb-4">
            Before sending A2P text messages, we will obtain your opt-in consent. We offer multiple
            mechanisms for gaining your opt-in consent:
          </p>
          <p className="text-gray-700 mb-2">
            <em>Online Forms</em>: You may opt-in through our website by completing an online form
            with your mobile phone number and consent.
          </p>
          <p className="text-gray-700 mb-4">
            <em>Phone or In-Person</em>: If you engage with us via phone or in-person, our
            representatives may request your consent verbally and record it accordingly.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">How We Use Your Information</h2>
          <p className="text-gray-700 mb-4">
            We use the information we collect for the following purposes:
          </p>
          <p className="text-gray-700 mb-2">
            <em>A2P Text Messaging</em>: To send you business related SMS messages regarding your
            work schedule and other information relating to the company operations, and/or your
            employment.
          </p>
          <p className="text-gray-700 mb-2">
            <em>Service Improvement</em>: To enhance and improve the quality of our A2P text
            messaging services.
          </p>
          <p className="text-gray-700 mb-4">
            <em>Employee Support</em>: To provide employee support and respond to your inquiries.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Disclosure of Your Information</h2>
          <p className="text-gray-700 mb-4">
            We may disclose your personal information to third parties in the following circumstances:
          </p>
          <p className="text-gray-700 mb-2">
            <em>Service Providers</em>: We may share your information with third-party service
            providers who assist us in delivering A2P text messaging services.
          </p>
          <p className="text-gray-700 mb-4">
            <em>Legal Compliance</em>: We may disclose your information as required by law, such as
            in response to a court order or legal process.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Your Consent</h2>
          <p className="text-gray-700 mb-4">
            By opting-in to our A2P text messaging services, you consent to the collection, use, and
            disclosure of your personal information as described in this Privacy Policy.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Security</h2>
          <p className="text-gray-700 mb-4">
            We implement reasonable security measures to protect your personal information from
            unauthorized access, disclosure, alteration, or destruction. Please be aware that no
            method of transmission over the internet or electronic storage is entirely secure.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Opt-out</h2>
          <p className="text-gray-700 mb-4">
            You have the right to opt-out of receiving A2P text messages at any time. To do so, you may:
          </p>
          <p className="text-gray-700 mb-2">
            Reply "STOP" to any text message from us. This will automatically opt you out of further
            messages.
          </p>
          <p className="text-gray-700 mb-4">
            Contact Us at info@rscminc.com to request to be removed from our A2P text messaging list.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Updates to this Privacy Policy</h2>
          <p className="text-gray-700 mb-4">
            We may update this Privacy Policy periodically to reflect changes in our practices or for
            legal reasons. The updated policy will be posted on our website, and the effective date
            will be revised accordingly.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Contact Us</h2>
          <p className="text-gray-700 mb-4">
            If you have any questions, concerns, or requests regarding this Privacy Policy or our A2P
            text messaging services, please contact us at info@rscminc.com.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SMSTerms;
